/**
 * Creative Studio PDP adapters — Vuex-backed implementations of the
 * chat-surface contract consumed by the shared PDP handlers in
 * pdp-generation-paths.js and create-pdp-image-set.js.
 *
 * Two factories are exposed:
 *   - createCreativeStudioPdpAdapter(store)        — for Vue components
 *     (NetworksPageV2 / NetworksDock) that hold a reference to the root store.
 *   - createCreativeStudioPdpContextAdapter(ctx)   — for Vuex actions inside
 *     the creativeStudio namespace, where `commit` / `dispatch` are already
 *     scoped to that namespace (no 'creativeStudio/' prefix needed).
 */

import { leaveAiStudio } from '@frontend/store/creative-studio/ai-studio-sessions-bridge';

// Adapter methods that WRITE to chat state. Wrapping these is how a whole flow
// gets conversation-guarded at once. (POR-13504)
const ADAPTER_WRITE_METHODS = [
  'addMessage',
  'addAndSaveMessage',
  'addAndSaveMessages',
  'saveMessages',
  'updateMessage',
  'updateAndSaveMessage',
  'setMessageDisabled',
  'saveConversationState',
  'setParsedPrompt',
];

/**
 * Wrap a PDP adapter so its chat-WRITE methods become no-ops once the active
 * conversation no longer matches `ownerConversationId` — i.e. the user clicked
 * "New" (or opened another chat) after this operation began. Reads and
 * non-writes (getMessages, setProcessing, pushPendingDesignJob, …) pass through
 * untouched. This is the widget-action counterpart of `withConversationGuard`
 * (which guards the sendMessage chat path): build it ONCE at the start of a
 * user action (e.g. onWidgetAction) with the then-active conversation id, and
 * pass it wherever `this._pdpAdapter` would go — every downstream async result
 * that resolves after "New" is then discarded instead of leaking into the
 * now-active chat. A null owner disables the guard. (POR-13504)
 */
export function withConversationGuardedAdapter(adapter, ownerConversationId) {
  if (!adapter) return adapter;
  const isStale = () => ownerConversationId != null
    && (typeof adapter.getActiveConversationId === 'function'
      ? adapter.getActiveConversationId()
      : null) !== ownerConversationId;
  const guarded = { ...adapter, isConversationStale: isStale };
  ADAPTER_WRITE_METHODS.forEach((method) => {
    if (typeof adapter[method] === 'function') {
      guarded[method] = (...args) => (isStale() ? undefined : adapter[method](...args));
    }
  });
  return guarded;
}

export function createCreativeStudioPdpAdapter(store) {
  return {
    getMessages: () => store.state.creativeStudio.messages,
    // The conversation a generation belongs to. Consumed by the shared PDP
    // handlers to discard late async results once the user has left this chat
    // ("New" / opened another conversation) instead of leaking them. (POR-13504)
    getActiveConversationId: () => (store.state.creativeStudio.activeConversation
      ? store.state.creativeStudio.activeConversation.id
      : null),
    getParsedPrompt: () => store.state.creativeStudio.parsedPrompt || {},
    getSelectedReferences: () => store.state.creativeStudio.selectedReferences || [],
    getSelectedTentpole: () => store.state.creativeStudio.selectedTentpole || null,
    setParsedPrompt: (parsed) => store.commit('creativeStudio/SET_PARSED_PROMPT', parsed),
    saveConversationState: () => {
      return store.dispatch('creativeStudio/saveConversationState');
    },
    addMessage: (msg) => store.commit('creativeStudio/ADD_MESSAGE', msg),
    addAndSaveMessage: (msg) => {
      return store.dispatch('creativeStudio/addAndSaveMessage', msg);
    },
    addAndSaveMessages: (messages) => {
      return store.dispatch('creativeStudio/addAndSaveMessages', messages);
    },
    updateMessage: (index, patch) =>
      store.commit('creativeStudio/UPDATE_MESSAGE', { index, patch }),
    updateAndSaveMessage: (index, patch) => {
      return store.dispatch('creativeStudio/updateAndSaveMessage', { index, patch });
    },
    setMessageDisabled: (index, disabled) => {
      return store.dispatch('creativeStudio/updateAndSaveMessage', {
        index,
        patch: { disabled },
      });
    },
    saveMessages: (messageOrMessages) => {
      return store.dispatch('creativeStudio/saveMessages', messageOrMessages);
    },
    setProcessing: (flag) =>
      store.commit('creativeStudio/SET_PROCESSING_CHAT_MESSAGE', flag),
    setPendingRegeneration: (payload) =>
      store.commit('creativeStudio/SET_PENDING_PDP_REGENERATION', payload),
    setPendingPdpRefine: (payload) =>
      store.commit('creativeStudio/SET_PENDING_PDP_REFINE', payload),
    setPendingPdpReference: (payload) =>
      store.commit('creativeStudio/SET_PENDING_PDP_REFERENCE', payload),
    setPendingPdpReferenceAsk: (payload) =>
      store.commit('creativeStudio/SET_PENDING_PDP_REFERENCE_ASK', payload),
    setChatText: (text) =>
      store.commit('creativeStudio/SET_CHAT_TEXT', text),
    setShowcaseTemplates: (templates) =>
      store.commit('creativeStudio/SET_SHOWCASE_TEMPLATES', templates),
    pushPendingDesignJob: (entry) => store.dispatch('creativeStudio/pushPendingDesignJob', entry),
    resolvePendingDesignJob: (uuid) => store.dispatch('creativeStudio/resolvePendingDesignJob', uuid),
    getSource: () => 'creative_studio_chat',
    supportsReferenceRowPicker: () => true,
    getCurrentLayoutId: () => null,
    refreshLayout: async () => {},
    navigateToLayout: (url) => {
      if (url) leaveAiStudio(url);
    },
    dispatch: (action, payload) => store.dispatch(action, payload),
  };
}

export function createCreativeStudioPdpContextAdapter({ state, commit, dispatch }) {
  return {
    getMessages: () => state.messages,
    // The conversation a generation belongs to. Consumed by the shared PDP
    // handlers to discard late async results once the user has left this chat
    // ("New" / opened another conversation) instead of leaking them. (POR-13504)
    getActiveConversationId: () => (state.activeConversation ? state.activeConversation.id : null),
    getParsedPrompt: () => state.parsedPrompt || {},
    getSelectedReferences: () => state.selectedReferences || [],
    getSelectedTentpole: () => state.selectedTentpole || null,
    setParsedPrompt: (parsed) => commit('SET_PARSED_PROMPT', parsed),
    saveConversationState: () => {
      return dispatch('saveConversationState');
    },
    addMessage: (msg) => commit('ADD_MESSAGE', msg),
    addAndSaveMessage: (msg) => {
      return dispatch('addAndSaveMessage', msg);
    },
    addAndSaveMessages: (messages) => {
      return dispatch('addAndSaveMessages', messages);
    },
    updateMessage: (index, patch) =>
      commit('UPDATE_MESSAGE', { index, patch }),
    updateAndSaveMessage: (index, patch) => {
      return dispatch('updateAndSaveMessage', { index, patch });
    },
    setMessageDisabled: (index, disabled) => {
      return dispatch('updateAndSaveMessage', {
        index,
        patch: { disabled },
      });
    },
    saveMessages: (messageOrMessages) => {
      return dispatch('saveMessages', messageOrMessages);
    },
    setProcessing: (flag) =>
      commit('SET_PROCESSING_CHAT_MESSAGE', flag),
    setPendingRegeneration: (payload) =>
      commit('SET_PENDING_PDP_REGENERATION', payload),
    setPendingPdpRefine: (payload) =>
      commit('SET_PENDING_PDP_REFINE', payload),
    setPendingPdpReference: (payload) =>
      commit('SET_PENDING_PDP_REFERENCE', payload),
    setPendingPdpReferenceAsk: (payload) =>
      commit('SET_PENDING_PDP_REFERENCE_ASK', payload),
    setChatText: (text) =>
      commit('SET_CHAT_TEXT', text),
    setShowcaseTemplates: (templates) =>
      commit('SET_SHOWCASE_TEMPLATES', templates),
    pushPendingDesignJob: (entry) => dispatch('pushPendingDesignJob', entry),
    resolvePendingDesignJob: (uuid) => dispatch('resolvePendingDesignJob', uuid),
    getSource: () => 'creative_studio_chat',
    supportsReferenceRowPicker: () => true,
    getCurrentLayoutId: () => null,
    refreshLayout: async () => {},
    navigateToLayout: (url) => {
      if (url) leaveAiStudio(url);
    },
    dispatch: (action, payload) => dispatch(action, payload),
  };
}
